import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosConn from "config/axiosConn";
import ReactApexChart from "react-apexcharts";
import "./ThemesAnalyticsDashboard.css";

// ─── Constants ─────────────────────────────────────────────────────────────────
// Themes Analytics only shows data from June 5, 2026 onward.
const MIN_THEMES_DATE = "2026-06-05";

const THEME_NAMES = [
  "Adopt Healthy Lifestyles",
  "Adopt Sustainable Food Systems",
  "Reduce Waste",
  "Reduce E-Waste",
  "Save Energy",
  "Save Water",
  "Say No to Single Use Plastic",
];

const THEME_ICONS = {
  "Adopt Healthy Lifestyles":       "🏃",
  "Adopt Sustainable Food Systems": "🌱",
  "Reduce Waste":                   "♻️",
  "Reduce E-Waste":                 "💻",
  "Save Energy":                    "⚡",
  "Save Water":                     "💧",
  "Say No to Single Use Plastic":   "🚫",
};

const THEME_COLORS = {
  "Adopt Healthy Lifestyles":       "#2563eb",
  "Adopt Sustainable Food Systems": "#0d9488",
  "Reduce Waste":                   "#ea580c",
  "Reduce E-Waste":                 "#7c3aed",
  "Save Energy":                    "#d97706",
  "Save Water":                     "#0891b2",
  "Say No to Single Use Plastic":   "#dc2626",
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 10_000_000) return (n / 10_000_000).toFixed(1) + "Cr";
  if (n >= 100_000)    return (n / 100_000).toFixed(1) + "L";
  if (n >= 1_000)      return n.toLocaleString("en-IN");
  return n.toString();
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// ─── Sub-component: KPI Card ────────────────────────────────────────────────────
function KpiCard({ icon, value, label, color = "#3b82f6" }) {
  return (
    <div className="tad-kpi-card" style={{ "--kpi-color": color }}>
      <span className="tad-kpi-icon">{icon}</span>
      <div className="tad-kpi-value">{fmt(value)}</div>
      <div className="tad-kpi-label">{label}</div>
    </div>
  );
}

// ─── Sub-component: Theme Card ──────────────────────────────────────────────────
function ThemeCard({ theme, onDrillDown }) {
  const color = theme.color || THEME_COLORS[theme.theme] || "#3b82f6";
  const icon  = THEME_ICONS[theme.theme] || "🌿";

  return (
    <div
      className="tad-theme-card"
      style={{
        "--theme-color": color,
        "--theme-color-glow": color + "18",
      }}
      onClick={() => onDrillDown(theme.theme)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onDrillDown(theme.theme)}
    >
      <div className="tad-theme-header">
        <div className="tad-theme-name">
          {icon} {theme.theme}
        </div>
        <div className="tad-theme-rank">
          <span className="tad-theme-rank-num">#{theme.rank}</span>
          <span className="tad-theme-rank-label">Rank</span>
        </div>
      </div>

      <div className="tad-theme-metrics">
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.registrations)}</div>
          <div className="tad-theme-metric-lbl">Events</div>
        </div>
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.participants)}</div>
          <div className="tad-theme-metric-lbl">Participants</div>
        </div>
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.events)}</div>
          <div className="tad-theme-metric-lbl">Activities</div>
        </div>
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.schools)}</div>
          <div className="tad-theme-metric-lbl">Schools</div>
        </div>
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.districts)}</div>
          <div className="tad-theme-metric-lbl">Districts</div>
        </div>
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.states)}</div>
          <div className="tad-theme-metric-lbl">States/UTs/ABs</div>
        </div>
      </div>

      <div className="tad-adoption-bar-wrap">
        <div className="tad-adoption-bar-header">
          <span className="tad-adoption-bar-label">Adoption Share</span>
          <span className="tad-adoption-bar-pct">{theme.adoptionPct}%</span>
        </div>
        <div className="tad-adoption-bar-bg">
          <div
            className="tad-adoption-bar-fill"
            style={{ width: `${Math.min(theme.adoptionPct, 100)}%`, background: color }}
          />
        </div>
      </div>

      <div className="tad-theme-card-footer">
        <span className="tad-view-link" style={{ color }}>
          View Detail →
        </span>
      </div>
    </div>
  );
}

// ─── Sub-component: State Leaderboard Row ───────────────────────────────────────
function RankBadge({ idx }) {
  const cls =
    idx === 0 ? "rank-1" :
    idx === 1 ? "rank-2" :
    idx === 2 ? "rank-3" : "rank-other";
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <span className={`tad-rank-badge ${cls}`}>
      {idx < 3 ? medals[idx] : idx + 1}
    </span>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────────────────
export default function ThemesAnalyticsDashboard() {
  const navigate   = useNavigate();
  const userData   = useSelector((state) => state.auth.user);

  const [data,              setData]              = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [fromDate,          setFromDate]          = useState(MIN_THEMES_DATE);
  const [toDate,            setToDate]            = useState("");

  // Derived
  const isStateAdmin = userData?.role === 4;
  const scopeLabel   = isStateAdmin && userData?.state
    ? userData.state
    : "National";

  // ── Fetch summary data ────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {};
      if (fromDate) payload.fromDate = fromDate + "T00:00:00.000Z";
      if (toDate) payload.toDate = toDate + "T23:59:59.999Z";
      const res = await axiosConn("post", "themes-dashboard/summary", payload);
      if (res?.data?.success) {
        setData(res.data);
      } else {
        setError("Failed to load dashboard data.");
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError(err?.response?.data?.error || "Network error — please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Download PDF Report ───────────────────────────────────────────────────
  const handleDownloadReport = async () => {
    if (downloadingReport) return;
    setDownloadingReport(true);
    try {
      const queryParams = new URLSearchParams();
      if (fromDate) queryParams.append("fromDate", fromDate);
      if (toDate) queryParams.append("toDate", toDate);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || "https://ecoclubs.education.gov.in/"}/api/reports/themes?${queryParams.toString()}`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error("Failed to generate report");
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "EcoClubs_Themes_Report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Report download error:", err);
      alert("Failed to download the Themes Analytics Report. Please try again.");
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDrillDown = (themeName) => {
    navigate(`/admin/themes-analytics/theme/${encodeURIComponent(themeName)}`);
  };

  // ── Donut chart config ──────────────────────────────────────────────────────
  const donutOptions = useMemo(() => {
    if (!data?.themes) return null;
    const filtered = data.themes.filter((t) => t.registrations > 0);
    return {
      options: {
        chart: { type: "donut", background: "transparent", toolbar: { show: false } },
        labels: filtered.map((t) => t.theme),
        colors: filtered.map((t) => t.color || THEME_COLORS[t.theme] || "#6b7280"),
        legend: {
          position: "bottom",
          labels: { colors: "#495057" },
          fontSize: "11px",
          fontFamily: "Inter, sans-serif",
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Total Events",
                  fontSize: "12px",
                  color: "#6c757d",
                  fontFamily: "Inter, sans-serif",
                  formatter: () => fmt(data.kpis?.totalRegistrations || 0),
                },
              },
            },
          },
        },
        dataLabels: { enabled: false },
        tooltip: {
          style: { fontFamily: "Inter, sans-serif" },
          y: { formatter: (v) => `${v} events` },
        },
        theme: { mode: "light" },
      },
      series: filtered.map((t) => t.registrations),
    };
  }, [data]);

  // ── Bar chart (schools per theme) ────────────────────────────────────────────
  const barOptions = useMemo(() => {
    if (!data?.themes) return null;
    const sorted = [...data.themes].sort((a, b) => b.schools - a.schools);
    return {
      options: {
        chart: { type: "bar", background: "transparent", toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "60%",
            distributed: true,
            borderRadius: 4,
          },
        },
        colors: sorted.map((t) => t.color || THEME_COLORS[t.theme] || "#6b7280"),
        xaxis: {
          categories: sorted.map((t) =>
            t.theme.length > 22 ? t.theme.slice(0, 20) + "…" : t.theme
          ),
          labels: { style: { colors: "#495057", fontSize: "11px", fontFamily: "Inter, sans-serif" } },
        },
        yaxis: { labels: { style: { colors: "#495057", fontSize: "11px", fontFamily: "Inter, sans-serif" } } },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          style: { fontSize: "11px", fontFamily: "Inter, sans-serif", colors: ["#ffffff"] },
          formatter: (v) => fmt(v),
        },
        tooltip: { style: { fontFamily: "Inter, sans-serif" } },
        grid: { borderColor: "#e9ecef" },
        theme: { mode: "light" },
      },
      series: [{ name: "Schools", data: sorted.map((t) => t.schools) }],
    };
  }, [data]);

  // ── Trend area chart ─────────────────────────────────────────────────────────
  const trendOptions = useMemo(() => {
    if (!data?.trend?.length) return null;
    return {
      options: {
        chart: { type: "area", background: "transparent", toolbar: { show: false }, zoom: { enabled: false } },
        colors: ["#28a745"],
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] },
        },
        stroke: { curve: "smooth", width: 2 },
        xaxis: {
          categories: data.trend.map((d) => fmtDate(d._id)),
          labels: { style: { colors: "#6c757d", fontSize: "10px", fontFamily: "Inter, sans-serif" }, rotate: -30 },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#6c757d", fontSize: "10px", fontFamily: "Inter, sans-serif" }, formatter: fmt } },
        grid: { borderColor: "#e9ecef", strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { style: { fontFamily: "Inter, sans-serif" }, x: { show: true } },
        theme: { mode: "light" },
      },
      series: [{ name: "Events", data: data.trend.map((d) => d.registrations) }],
    };
  }, [data]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="tad-page">
      {/* ── HEADER ── */}
      <div className="tad-header">
        <div className="tad-header-left">
          <div className="tad-badge">
            <span className="dot" /> Live Dashboard
          </div>
          <h1>🌍 Themes Analytics Dashboard</h1>
          {/* <p className="tad-header-sub">
            Mission LiFE Campaign · Eco Clubs · All 7 Themes
          </p> */}
        </div>

        <div className="tad-header-actions" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {isStateAdmin && (
              <div className="tad-scope-badge">
                🗺️ Scope: {scopeLabel}
              </div>
            )}
            <button className="tad-btn tad-btn-ghost" onClick={() => navigate("/admin/environment-day-dashboard")}>
              ← Back
            </button>
            <button className="tad-btn tad-btn-primary" onClick={fetchData} disabled={loading}>
              🔄 Refresh
            </button>
            <button
              className="tad-btn tad-btn-download"
              onClick={handleDownloadReport}
              disabled={downloadingReport || loading}
              title="Download Themes Analytics PDF Report"
            >
              {downloadingReport ? "⏳ Generating..." : "📥 Download Report"}
            </button>
          </div>

          <div className="tad-date-filters" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ fontSize: "10px", fontWeight: "600", color: "#64748b", marginBottom: "2px" }}>From Date</label>
              <input
                type="date"
                value={fromDate}
                min={MIN_THEMES_DATE}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "6px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: "12px",
                  outline: "none",
                  color: "#334155"
                }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ fontSize: "10px", fontWeight: "600", color: "#64748b", marginBottom: "2px" }}>To Date</label>
              <input
                type="date"
                value={toDate}
                min={MIN_THEMES_DATE}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "6px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: "12px",
                  outline: "none",
                  color: "#334155"
                }}
              />
            </div>
            {(fromDate !== MIN_THEMES_DATE || toDate) && (
              <button
                onClick={() => {
                  setFromDate(MIN_THEMES_DATE);
                  setToDate("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#dc2626",
                  fontSize: "11px",
                  fontWeight: "700",
                  cursor: "pointer",
                  marginTop: "14px",
                  padding: "0"
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>



      {/* ── BODY ── */}
      <div className="tad-body">
        {loading && (
          <div className="tad-loading">
            <div className="tad-spinner" />
            <span className="tad-loading-text">Loading dashboard data…</span>
          </div>
        )}

        {!loading && error && (
          <div className="tad-error">
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Failed to load data</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{error}</div>
            <button className="tad-btn tad-btn-primary" style={{ marginTop: 16 }} onClick={fetchData}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* ── NATIONAL KPIs ── */}
            <div className="tad-section-title">📊 Executive Summary</div>
            <div className="tad-kpi-grid">
              <KpiCard icon="📋" value={data.kpis?.totalRegistrations} label="Total Events" color="#3b82f6" />
              <KpiCard icon="🏫" value={data.kpis?.totalSchools}       label="Schools Participated"  color="#0d9488" />
              <KpiCard icon="🗺️" value={data.kpis?.totalDistricts}    label="Districts Covered"     color="#7c3aed" />
              <KpiCard icon="🌐" value={data.kpis?.totalStates}        label="States/UTs/ABs"          color="#d97706" />
              <KpiCard icon="👥" value={data.kpis?.totalParticipants}  label="Total Participants"    color="#0891b2" />
              <KpiCard icon="🎯" value={data.kpis?.totalEvents}        label="Total Activities"      color="#ea580c" />
              <KpiCard icon="🏆" value={THEME_NAMES.length}            label="Active Themes"         color="#dc2626" />
            </div>

            {/* ── THEME CARDS ── */}
            <div className="tad-section-title">🌿 Theme Adoption Analysis</div>
            <div className="tad-themes-grid">
              {data.themes.map((theme) => (
                <ThemeCard
                  key={theme.theme}
                  theme={theme}
                  onDrillDown={handleDrillDown}
                />
              ))}
            </div>

            {/* ── CHARTS ROW ── */}
            <div className="tad-section-title">📈 Visual Analytics</div>
            <div className="tad-charts-row">
              {/* Donut */}
              {donutOptions && (
                <div className="tad-chart-card">
                  <div className="tad-chart-title">🎯 Theme Distribution</div>
                  <ReactApexChart
                    options={donutOptions.options}
                    series={donutOptions.series}
                    type="donut"
                    height={320}
                  />
                </div>
              )}

              {/* Bar – Schools per theme */}
              {barOptions && (
                <div className="tad-chart-card">
                  <div className="tad-chart-title">🏫 Schools Adopted per Theme</div>
                  <ReactApexChart
                    options={barOptions.options}
                    series={barOptions.series}
                    type="bar"
                    height={320}
                  />
                </div>
              )}
            </div>

            {/* ── TREND CHART ── */}
            {trendOptions && (
              <div className="tad-trend-wrap">
                <div className="tad-section-title">📅 Event Trend</div>
                <div className="tad-chart-card">
                  <div className="tad-chart-title">Daily Events</div>
                  <ReactApexChart
                    options={trendOptions.options}
                    series={trendOptions.series}
                    type="area"
                    height={220}
                  />
                </div>
              </div>
            )}

            {/* ── STATE LEADERBOARD ── */}
            {data.stateLeaderboard?.length > 0 && (
              <>
                <div className="tad-section-title">🏆 Top States/UTs/ABs by Events</div>
                <div className="tad-table-card">
                  <div className="tad-table-header">
                     <span className="tad-table-title">States/UTs/ABs Performance Leaderboard</span>
                  </div>
                  <table className="tad-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>States/UTs/ABs</th>
                        <th>Events</th>
                        <th>Schools</th>
                        <th>Participants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.stateLeaderboard.map((s, idx) => (
                        <tr key={s.state || idx}>
                          <td><RankBadge idx={idx} /></td>
                          <td style={{ fontWeight: 600, color: "#212529" }}>{s.state}</td>
                          <td>{fmt(s.registrations)}</td>
                          <td>{fmt(s.schools)}</td>
                          <td>{fmt(s.participants)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ── AI INSIGHTS ── */}
            {data.themes?.length > 0 && (() => {
              const topTheme = data.themes[0];
              const totalRegs = data.kpis?.totalRegistrations || 0;
              return (
                <div className="tad-chart-card" style={{ marginTop: 24 }}>
                  <div className="tad-chart-title">🤖 AI Insights</div>
                  <div style={{ fontSize: 14, color: "#6c757d", lineHeight: 1.7 }}>
                    <p>
                      🏆 <strong style={{ color: "#212529" }}>{topTheme.theme}</strong> leads all themes with{" "}
                      <strong style={{ color: topTheme.color }}>{fmt(topTheme.registrations)}</strong> events
                      ({topTheme.adoptionPct}% adoption share).
                    </p>
                    <p>
                      🏫 A total of <strong style={{ color: "#212529" }}>{fmt(data.kpis?.totalSchools)}</strong> schools
                      across <strong style={{ color: "#212529" }}>{fmt(data.kpis?.totalStates)}</strong> States/UTs have
                      adopted Mission LiFE themes since May 15, 2026.
                    </p>
                    <p>
                      👥 Over <strong style={{ color: "#212529" }}>{fmt(data.kpis?.totalParticipants)}</strong> participants
                      have been engaged through{" "}
                      <strong style={{ color: "#212529" }}>{fmt(data.kpis?.totalEvents)}</strong> events.
                    </p>
                    {data.themes.length >= 2 && (
                      <p>
                        💡 The theme with the lowest adoption is{" "}
                        <strong style={{ color: "#212529" }}>
                          {data.themes[data.themes.length - 1].theme}
                        </strong>{" "}
                        at {data.themes[data.themes.length - 1].adoptionPct}% — consider targeted outreach to boost its participation.
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
